import React, { useEffect, useRef, useState } from 'react';
import { X, Gift, ShoppingBag } from 'lucide-react';
import { getRewardFeed, RewardFeedItem } from '../../api/payment/reward-feed.api';

// Màu theo độ hiếm (khớp toàn hệ thống).
const RARITY: Record<string, { label: string; color: string; bg: string }> = {
  Common: { label: 'Cổ Điển', color: '#9CA3AF', bg: 'rgba(156,163,175,.14)' },
  Rare: { label: 'Hiếm', color: '#3B82F6', bg: 'rgba(59,130,246,.14)' },
  Epic: { label: 'Sử Thi', color: '#8B5CF6', bg: 'rgba(139,92,246,.14)' },
  Legendary: { label: 'Huyền Thoại', color: '#F59E0B', bg: 'rgba(245,158,11,.14)' },
  Mythic: { label: 'Thần Thoại', color: '#EC4899', bg: 'rgba(236,72,153,.14)' },
};

const POLL_MS = 25000; // làm mới feed
const SHOW_MS = 5000; // hiện mỗi tin ~5s
const GAP_MS = 7000; // rồi ẩn ~7s trước khi nhá tin kế

// Che email cho riêng tư (chỉ hiện phần trước @).
const cleanName = (s: string) => (s && s.includes('@') ? s.split('@')[0] : s || 'Một thành viên');

const RewardTicker: React.FC = () => {
  const [items, setItems] = useState<RewardFeedItem[]>([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('reward-ticker-closed') === '1';
    } catch {
      return false;
    }
  });
  const idxRef = useRef(0);

  // Poll feed.
  useEffect(() => {
    let alive = true;
    const load = () =>
      getRewardFeed(20)
        .then((d) => alive && setItems(Array.isArray(d) ? d : []))
        .catch(() => {});
    load();
    const poll = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, []);

  // Chu kỳ nhá: hiện SHOW_MS → ẩn GAP_MS → sang tin kế → lặp.
  useEffect(() => {
    if (closed || items.length === 0) {
      setVisible(false);
      return;
    }
    let showing = false;
    let timer: any;
    const tick = () => {
      if (showing) {
        setVisible(false); // đang hiện → ẩn
        showing = false;
        timer = setTimeout(tick, GAP_MS);
      } else {
        idxRef.current = (idxRef.current + 1) % items.length; // sang tin kế
        setIdx(idxRef.current);
        setVisible(true);
        showing = true;
        timer = setTimeout(tick, SHOW_MS);
      }
    };
    tick(); // nhá ngay tin đầu
    return () => clearTimeout(timer);
  }, [items.length, closed]);

  if (closed || items.length === 0) return null;

  const it = items[Math.min(idx, items.length - 1)];
  const rc = RARITY[it.rarity] || RARITY.Common;
  const isWin = it.kind === 'win';

  const close = () => {
    try {
      sessionStorage.setItem('reward-ticker-closed', '1');
    } catch {
      /* ignore */
    }
    setClosed(true);
  };

  return (
    <div
      className="fixed bottom-4 z-[95] hidden sm:block max-w-[320px]"
      style={{ left: 'calc(var(--w-side-sm, 88px) + 12px)' }}
    >
      <div
        className="relative flex items-center gap-3 rounded-2xl border bg-white/95 dark:bg-slate-800/95 backdrop-blur px-3 py-2.5 shadow-xl transition-all duration-500 ease-out"
        style={{
          borderColor: rc.color + '55',
          transform: visible ? 'translateX(0)' : 'translateX(-120%)',
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? 'auto' : 'none',
        }}
      >
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ background: rc.bg, color: rc.color }}
        >
          {isWin ? <Gift size={20} /> : <ShoppingBag size={20} />}
        </div>
        <div className="min-w-0 pr-4">
          <div className="text-[13px] leading-snug ui-text">
            <span className="font-bold">{cleanName(it.displayName)}</span>{' '}
            {isWin ? 'vừa trúng' : 'vừa mua'}{' '}
            <span className="font-semibold" style={{ color: rc.color }}>
              {it.itemName}
            </span>
            {isWin && it.source ? <span className="ui-muted"> từ {it.source}</span> : null}
          </div>
          <div className="text-[11px]" style={{ color: rc.color }}>
            {rc.label} · {it.itemType === 'Nametag' ? 'Khung tên' : 'Khung avatar'} 🎉
          </div>
        </div>
        <button
          onClick={close}
          aria-label="Đóng"
          className="absolute right-1.5 top-1.5 rounded p-0.5 ui-muted hover:bg-black/5 dark:hover:bg-white/10"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default RewardTicker;
